import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useNavigate } from "react-router-dom";
import Banner1 from "../assets/Banner_1.jpeg";
import Banner2 from "../assets/Banner_2.jpeg";

import DashboardLayout from "../layout/DashboardLayout";
import { upcomingEventsData } from "../data/upcomingEventsData";

function fmtDate(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtTime(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}


const mockBanners = [
  { id: "b1", title: "Art Classes", image: Banner1 },
  { id: "b2", title: "Weekend Events", image: Banner2 },
//  { id: "b3", title: "Creative Workshops", image: BannerMain }
];

export default function Events() {
  const navigate = useNavigate();

  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setEvents(upcomingEventsData);
      setLoading(false);
    }, 200);
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    for (const ev of upcomingEventsData) {
      if (ev?.category) set.add(String(ev.category));
    }
    return ["", ...Array.from(set)];
  }, []);

  function applyFilters() {
    let list = [...upcomingEventsData];

    if (category) {
      list = list.filter((e) => String(e.category).toLowerCase() === String(category).toLowerCase());
    }

    if (search) {
      const s = String(search).toLowerCase();
      list = list.filter((e) => {
        return (
          String(e.title || "").toLowerCase().includes(s) ||
          String(e.description || "").toLowerCase().includes(s) ||
          String(e.location || "").toLowerCase().includes(s)
        );
      });
    }

    setEvents(list);
  }

  const isList = view === "list";
  const gridCols = isList ? "repeat(1, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))";

  return (
    <DashboardLayout title="Dashboard">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 3 }}>
        {/* LEFT */}
        <Box sx={{ minWidth: 0 }}>
          {/* Header strip */}
          <Card sx={{ bgcolor: "action.selected" }}>
            <CardContent sx={{ py: 2.25 }}>
              <Typography sx={{ fontWeight: 900 }}>Events & Experiences</Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13, color: "text.secondary", lineHeight: 1.6 }}>
                Your upcoming and past event registrations at NeherBaari. Review your scheduled experiences, add them to your
                calendar, and revisit memorable moments from your journey with us.
              </Typography>
            </CardContent>
          </Card>

          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Upcoming Events & Experiences</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            {/* Controls */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "flex-end",
                mb: 1.5,
                flexWrap: "wrap"
              }}
            >
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events..."
                sx={{ width: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

              <Select value={category} onChange={(e) => setCategory(e.target.value)} displayEmpty sx={{ minWidth: 160 }}>
                <MenuItem value="">All Categories</MenuItem>
                {categories
                  .filter((c) => c)
                  .map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
              </Select>

              <Button variant="contained" onClick={applyFilters} sx={{ px: 2.2 }}>
                Apply
              </Button>

              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(_, v) => v && setView(v)}
                size="small"
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}
              >
                <ToggleButton value="grid" sx={{ px: 1.1 }}>
                  <GridViewRoundedIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list" sx={{ px: 1.1 }}>
                  <ViewListRoundedIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Events grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: gridCols }, gap: 2 }}>
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i}>
                    <Box sx={{ p: 2 }}>
                      <Skeleton variant="rounded" height={130} />
                      <Skeleton sx={{ mt: 1.1 }} width="40%" />
                      <Skeleton sx={{ mt: 0.6 }} width="80%" />
                      <Skeleton sx={{ mt: 0.9 }} width="60%" />
                    </Box>
                  </Card>
                ))}

              {!loading && events.length === 0 && (
                <Card>
                  <CardContent sx={{ py: 2.2 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 900 }}>No events found</Typography>
                    <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "text.secondary" }}>
                      Try adjusting search or category filters.
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {!loading &&
                events.map((ev) => (
                  <Card
                    key={ev.id}
                    sx={{
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "transform 0.2s ease",
                      "&:hover": { transform: "translateY(-2px)" }
                    }}
                    onClick={() => navigate(`/events/${ev.id}`)}
                  >
                    <Box
                      sx={{
                        height: isList ? 150 : 130,
                        backgroundImage: `url(${ev.coverImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center"
                      }}
                    />

                    <CardContent sx={{ py: 2 }}>
                      <Chip size="small" label={ev.category} />

                      <Typography sx={{ mt: 1.2, fontSize: 13.5, fontWeight: 900 }}>{ev.title}</Typography>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.7 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.8 }} />
                          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                            {fmtDate(ev.dateTime)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <AccessTimeOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.8 }} />
                          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                            {fmtTime(ev.dateTime)}
                          </Typography>
                        </Box>

                        {!!ev.location && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                            <LocationOnOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.8 }} />
                            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                              {ev.location}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {isList && (
                        <Typography sx={{ mt: 1.1, fontSize: 12.5, color: "text.secondary", lineHeight: 1.6 }}>
                          {String(ev.shortDescription || ev.description || "").slice(0, 160)}
                          {String(ev.shortDescription || ev.description || "").length > 160 ? "…" : ""}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
            </Box>

            {/* Featured */}
            <Box sx={{ mt: 3 }}>
              <Box className="sectionHeader">
                <Typography className="sectionHeaderTitle">Event & Activity Highlights</Typography>
                <Box className="sectionHeaderLine" />
              </Box>

              <Card>
                <CardContent
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1.3fr" },
                    gap: 3
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 900 }}>
                      Featured: Innovation Leadership Series
                    </Typography>

                    <Typography sx={{ mt: 1, fontSize: 12.5, color: "text.secondary", lineHeight: 1.55 }}>
                      A curated monthly program bringing together leaders and builders to share insights, learnings and
                      practical frameworks.
                    </Typography>

                    <Typography sx={{ mt: 1.4, fontSize: 12.5, color: "text.secondary" }}>
                      Monthly series • Next session: Jan 25
                    </Typography>
                    <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "text.secondary" }}>
                      Limited to 30 participants
                    </Typography>

                    <Button variant="contained" sx={{ mt: 2.2, px: 2.4 }}>
                      Learn More
                    </Button>
                  </Box>

                  <Box
                    className="rounded-image"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      minHeight: 150,
                      backgroundImage:
                        "url(https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=70)",
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>

{/* RIGHT banners */}
<Box
  sx={{
    display: { xs: "none", lg: "flex" },
    flexDirection: "column",
    gap: 2
  }}
>
  {mockBanners.map((b) => (
    <Card
      key={b.id}
      elevation={0}
      sx={{
        overflow: "hidden",
        bgcolor: "background.paper"
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: "action.hover"
        }}
      >
        <Box
          sx={{
            height: 320,
            borderRadius: 0,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            backgroundImage: `url(${b.image})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain"
          }}
        />
      </Box>

      <Box sx={{ p: 1.5, pt: 1.2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/events")}
          sx={{
            borderColor: "primary.main",
            color: "primary.main",
            fontWeight: 800,
            borderRadius: 14,
            py: 1
          }}
        >
          Learn More
        </Button>
      </Box>
    </Card>
  ))}
</Box>


      </Box>
    </DashboardLayout>
  );
}
